import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { EmptyState } from '@vst/beam';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { NetworkLink } from '@app/router/constants';
import { useNavigate } from '@app/hooks';
import { GET_MODEM_STATUS, REBOOT_MODEM, resetModemIssue } from '@app/redux/network';

import { Mutation, Query } from '@mfe/shared/schema-types';

import { RebootError } from './reboot-error';

import styles from './styles.module.scss';

enum RebootStates {
  NOT_ISSUED = 'NOT_ISSUED',
  ISSUING = 'ISSUING',
  PENDING_OFFLINE = 'PENDING_OFFLINE',
  PENDING_ONLINE = 'PENDING_ONLINE',
  ERROR = 'ERROR',
  SUCCEEDED = 'SUCCEEDED',
}

const ModemRebootPage = () => {
  const { t } = useTranslation('Network', { keyPrefix: 'rebootModem' });

  const { replace } = useNavigate();
  const dispatch = useDispatch();

  const { rebootModem, rebootState, setRebootState } = useModemRebootState();

  useEffect((): void => {
    if (rebootState === RebootStates.NOT_ISSUED) {
      rebootModem().then((result) => {
        setRebootState(RebootStates.ISSUING);

        if (result?.data?.rebootModem?.rebootSuccess) {
          setRebootState(RebootStates.PENDING_OFFLINE);
        } else {
          setRebootState(RebootStates.ERROR);
        }
      });
    }

    if (rebootState === RebootStates.SUCCEEDED) {
      setRebootState(RebootStates.NOT_ISSUED);
      dispatch(resetModemIssue());
      replace(NetworkLink.Network);
    }
  }, [rebootState]);

  if (rebootState === RebootStates.ERROR) {
    return <RebootError />;
  }

  return (
    <div className={styles['pageContent']}>
      <EmptyState icon={ModemRebootAnimation} title={t('page.title')} description={t('page.description')} />
    </div>
  );
};

const useModemRebootState = () => {
  const [rebootState, setRebootState] = useState<RebootStates>(RebootStates.NOT_ISSUED);

  const [rebootModem] = useMutation<Mutation>(REBOOT_MODEM);

  const modemStatusQuery = useQuery<Query>(GET_MODEM_STATUS, {
    notifyOnNetworkStatusChange: true,
    variables: { refetchData: true },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const modemIsOnline = modemStatusQuery.data?.getModemStatus?.isOnline;

    if (modemStatusQuery.loading || !modemStatusQuery.data?.getModemStatus)
      return () => {
        // empty to keep consistent return value
      };

    let timeout: NodeJS.Timeout;
    const refetchModemStatusAfterTenSeconds = () => setTimeout((): any => modemStatusQuery.refetch(), 10 * 1000);

    // If it's supposed to go offline...
    // Check if it's offline and if not check again in 10s
    if (rebootState === RebootStates.PENDING_OFFLINE) {
      if (modemIsOnline) {
        timeout = refetchModemStatusAfterTenSeconds();
      } else {
        setRebootState(RebootStates.PENDING_ONLINE);
      }
    }

    // If it's supposed to go online...
    // Check if it is online and if not check again in 10s
    if (rebootState === RebootStates.PENDING_ONLINE) {
      if (modemIsOnline) {
        setRebootState(RebootStates.SUCCEEDED);
      } else {
        timeout = refetchModemStatusAfterTenSeconds();
      }
    }

    return (): void => {
      if (timeout) clearTimeout(timeout);
    };
  }, [rebootState, modemStatusQuery]);

  return { rebootState, setRebootState, rebootModem };
};

export default ModemRebootPage;

const ModemRebootAnimation = () => {
  return (
    <svg
      width="100"
      height="100"
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles['modemAnimation']}
    >
      <g clipPath="url(#clip0_5644_15917)">
        <path
          d="M3.7169 57.3818C3.39467 57.3835 3.08613 57.5124 2.85827 57.7405C2.63042 57.9686 2.50165 58.2775 2.5 58.6001V80.7933C2.50165 81.1159 2.63042 81.4249 2.85827 81.653C3.08613 81.8811 3.39467 82.01 3.7169 82.0116H6.32636V88.5804C6.31664 88.9528 6.45415 89.3138 6.70898 89.5852C6.8378 89.7158 6.9911 89.8196 7.1601 89.8907C7.3291 89.9618 7.51048 89.9989 7.6938 89.9996H16.3564C16.5451 90.0038 16.7326 89.9692 16.9074 89.8979C17.0822 89.8266 17.2405 89.7202 17.3725 89.5852C17.5023 89.4548 17.6051 89.3 17.6751 89.1297C17.745 88.9595 17.7808 88.7771 17.7803 88.593V81.9802H82.207V88.5616C82.2022 88.7504 82.2352 88.9383 82.3042 89.1141C82.3731 89.2899 82.4766 89.4501 82.6084 89.5852C82.7373 89.7158 82.8906 89.8196 83.0596 89.8907C83.2286 89.9618 83.4099 89.9989 83.5933 89.9996H92.2495C92.4392 90.0039 92.6277 89.9694 92.8035 89.8981C92.9793 89.8268 93.1388 89.7204 93.272 89.5852C93.5329 89.321 93.6794 88.9645 93.6797 88.593V81.9802H96.2829C96.6054 81.9796 96.9145 81.8509 97.1422 81.6223C97.2562 81.51 97.3465 81.3761 97.4079 81.2283C97.4693 81.0805 97.5005 80.922 97.4998 80.7619V58.6001C97.5028 58.4408 97.4729 58.2826 97.412 58.1354C97.3557 57.9865 97.268 57.8516 97.1548 57.7398C97.0429 57.6281 96.911 57.5387 96.7659 57.476C96.6193 57.413 96.4613 57.381 96.3017 57.3818H88.9376V13.523C88.9376 12.5887 88.5668 11.6926 87.9069 11.0319C87.247 10.3712 86.3519 10 85.4186 10C84.4853 10 83.5902 10.3712 82.9303 11.0319C82.2704 11.6926 81.8996 12.5887 81.8996 13.523V57.3818H18.1441V13.523C18.1441 12.5887 17.7733 11.6926 17.1134 11.0319C16.4535 10.3712 15.5584 10 14.6251 10C13.6918 10 12.7968 10.3712 12.1368 11.0319C11.4769 11.6926 11.1061 12.5887 11.1061 13.523V57.3818H3.7169ZM92.1617 88.3481H83.7752V81.9802H92.1367L92.1617 88.3481ZM16.2623 88.3481H7.86941V81.9802H16.2372L16.2623 88.3481ZM96.0069 80.4291H4.06817V58.9267H95.9442L96.0069 80.4291ZM91.7038 68.1958H85.2994L85.6256 65.8974H91.6788L91.7038 68.1958ZM82.3701 68.1958H75.7838L76.041 65.8974H82.3701V68.1958ZM72.7666 68.1958H66.2242L66.4061 65.8974H72.7415L72.7666 68.1958ZM62.5861 68.1958H56.0436L56.2255 65.8974H62.5359L62.5861 68.1958ZM53.6726 68.1958H47.1302L47.312 65.8974H53.6475L53.6726 68.1958ZM44.0691 68.1958H37.5894L37.7086 65.8974H44.044L44.0691 68.1958ZM34.4656 68.1958H28.0361L28.0863 65.8974H34.428L34.4656 68.1958ZM24.8621 68.1958H18.4891V65.8974H24.8371L24.8621 68.1958ZM14.7067 68.1958H8.32735V65.8974H14.6816L14.7067 68.1958ZM87.4384 57.3818H83.4866V13.523C83.4852 13.2629 83.5354 13.0051 83.6345 12.7647C83.7336 12.5243 83.8795 12.306 84.0637 12.1226C84.4341 11.7525 84.9361 11.5447 85.4594 11.5447C85.9827 11.5447 86.4846 11.7525 86.8551 12.1226C87.0393 12.306 87.1852 12.5243 87.2842 12.7647C87.3833 13.0051 87.4336 13.2629 87.4321 13.523L87.4384 57.3818ZM16.5885 57.3818H12.6555V13.523C12.6546 13.263 12.7052 13.0054 12.8042 12.765C12.9032 12.5246 13.0487 12.3062 13.2324 12.1224C13.416 11.9385 13.6342 11.7929 13.8743 11.6937C14.1144 11.5946 14.3717 11.544 14.6314 11.5448C15.1549 11.5465 15.6565 11.7554 16.0267 12.1261C16.3969 12.4967 16.6056 12.9989 16.6073 13.523L16.5885 57.3818Z"
          fill="#00819D"
        />
        <path
          className={styles['path3']}
          d="M49.9905 40.3319C51.7484 40.336 53.458 40.9087 54.8644 41.9647L55.8366 40.7589C54.1444 39.5032 52.0938 38.8253 49.9874 38.8253C47.881 38.8253 45.8303 39.5032 44.1381 40.7589L45.1103 41.9647C46.5184 40.9074 48.2304 40.3346 49.9905 40.3319Z"
          fill="#00819D"
        />

        <path
          className={styles['path2']}
          d="M49.9905 32.9781C53.6804 32.9808 57.245 34.319 60.0268 36.7461L61.0618 35.5843C57.9956 32.9075 54.065 31.4327 49.9968 31.4327C45.9286 31.4327 41.9979 32.9075 38.9318 35.5843L39.9668 36.7461C42.7456 34.3224 46.305 32.9844 49.9905 32.9781Z"
          fill="#00819D"
        />

        <path
          className={styles['path1']}
          d="M49.9905 25.863C55.5607 25.8488 60.9333 27.9285 65.0449 31.6908L66.0925 30.5604C61.6916 26.5747 55.9688 24.3677 50.0344 24.3677C44.1 24.3677 38.3772 26.5747 33.9763 30.5604L35.0239 31.6908C39.1151 27.9522 44.4513 25.8744 49.9905 25.863Z"
          fill="#00819D"
        />
      </g>
      <defs>
        <clipPath id="clip0_5644_15917">
          <rect width="100" height="100" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};
